// Advisor "My Founders" rollup page — PRD docs/founder-advisor-pipe-prd.md §7.1.
// Phase 1.0 surface: the advisor's primary triage inbox. Default sort is
// SLA breach severity DESC, then hours-since-last-touch DESC — i.e. the
// founders most likely to be slipping bubble to the top.
//
// Filters in 1.0: cohort multi-select, activation chip, SLA chip.
// Filtering happens client-side because the backend already returns the
// banker's full covered set in one shot — paginating per-filter would be
// premature for the May Deal cohort scale (~20 founders/advisor).
//
// Click a row → opens the read-only FounderDetailDrawer (sections 1–8).
// Phase 1.0 has no row-level actions — touch-write paths land in 1.1.
//
// Theme: semantic shadcn tokens only. Registered in
// scripts/check-contrast.mjs RULE3_FILES — no raw hex permitted except
// brand orange (which we route through `text-primary`/`bg-primary`).

import { useEffect, useMemo, useState } from "react";
import { Users, ChevronDown, ChevronUp, Inbox, AlertTriangle, ShieldCheck, CheckCircle2 } from "lucide-react";
import ProgressLoader from "@/components/progress-loader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import FounderDetailDrawer from "@/components/banker/founder-detail-drawer";
import type { FounderRollupItem, MyFoundersResponse, ActivationState, SlaStatus, Signal } from "@/types/advisor-pipe";

const BASE = import.meta.env.BASE_URL;

type SortKey = "slaSeverity" | "lastTouch" | "company" | "activation";
type SortDir = "asc" | "desc";

const SLA_SEVERITY: Record<SlaStatus, number> = { breach: 2, at_risk: 1, in_sla: 0 };
const ACT_SEVERITY: Record<ActivationState, number> = { stuck: 3, at_risk: 2, onboarding: 1, activated: 0 };

function slaBadge(s: SlaStatus) {
  if (s === "breach") return <Badge variant="destructive">SLA breach</Badge>;
  if (s === "at_risk") return <Badge variant="secondary">SLA at risk</Badge>;
  return <Badge variant="outline">in SLA</Badge>;
}
function activationBadge(s: ActivationState) {
  if (s === "activated") return <Badge>activated</Badge>;
  if (s === "stuck") return <Badge variant="destructive">stuck</Badge>;
  if (s === "at_risk") return <Badge variant="secondary">at risk</Badge>;
  return <Badge variant="outline">onboarding</Badge>;
}
function signalChip(s: Signal) {
  const cls =
    s === "green" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
    : s === "amber" ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30"
    : "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30";
  return <span className={`inline-block w-2 h-2 rounded-full border ${cls}`} title={s} />;
}
function fmtAgo(hours: number | null): string {
  if (hours == null) return "never";
  if (hours < 1) return "just now";
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export default function BankerMyFounders() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<FounderRollupItem[]>([]);

  // Filters
  const [q, setQ] = useState("");
  const [cohortFilter, setCohortFilter] = useState<Set<number>>(new Set());
  const [activationFilter, setActivationFilter] = useState<ActivationState | "all">("all");
  const [slaFilter, setSlaFilter] = useState<SlaStatus | "all">("all");

  // Sort
  const [sortKey, setSortKey] = useState<SortKey>("slaSeverity");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Drawer
  const [openRow, setOpenRow] = useState<FounderRollupItem | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const token = localStorage.getItem("banker_token");
      if (!token) { setError("Not signed in"); setLoading(false); return; }
      try {
        const r = await fetch(`${BASE}api/banker/my-founders`, { headers: { "x-banker-token": token } });
        if (!r.ok) {
          if (!cancelled) setError(`Failed to load (${r.status})`);
        } else {
          const j: MyFoundersResponse = await r.json();
          if (!cancelled) setData(j.founders || []);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? "Network error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const cohorts = useMemo(() => {
    const m = new Map<number, string>();
    for (const f of data) {
      if (f.cohortId != null && f.cohortName) m.set(f.cohortId, f.cohortName);
    }
    return Array.from(m.entries()).map(([id, name]) => ({ id, name }));
  }, [data]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return data.filter(f => {
      if (cohortFilter.size > 0 && (f.cohortId == null || !cohortFilter.has(f.cohortId))) return false;
      if (activationFilter !== "all" && f.activationState !== activationFilter) return false;
      if (slaFilter !== "all" && f.slaStatus !== slaFilter) return false;
      if (ql && !`${f.companyName} ${f.founderName ?? ""}`.toLowerCase().includes(ql)) return false;
      return true;
    });
  }, [data, q, cohortFilter, activationFilter, slaFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "slaSeverity") {
        cmp = SLA_SEVERITY[a.slaStatus] - SLA_SEVERITY[b.slaStatus];
        if (cmp === 0) cmp = (a.hoursSinceLastTouch ?? -1) - (b.hoursSinceLastTouch ?? -1);
      } else if (sortKey === "lastTouch") {
        cmp = (a.hoursSinceLastTouch ?? -1) - (b.hoursSinceLastTouch ?? -1);
      } else if (sortKey === "company") {
        cmp = a.companyName.localeCompare(b.companyName);
      } else {
        cmp = ACT_SEVERITY[a.activationState] - ACT_SEVERITY[b.activationState];
      }
      return sortDir === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  }

  function toggleCohort(id: number) {
    setCohortFilter(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  if (loading) {
    return (
      <div className="space-y-5">
        <header className="flex items-center gap-2">
          <Users className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold">My founders</h1>
        </header>
        <ProgressLoader
          active={true}
          title="Loading your founder portfolio"
          stages={["Pulling your portfolio", "Computing activation state", "Checking SLA"]}
          stageIntervalMs={900}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-5">
        <header className="flex items-center gap-2">
          <Users className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold">My founders</h1>
        </header>
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-6 text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> {error}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="space-y-5">
        <header className="flex items-center gap-2">
          <Users className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold">My founders</h1>
        </header>
        <div className="rounded-xl border bg-card p-12 text-center text-muted-foreground flex flex-col items-center gap-2">
          <Inbox className="w-8 h-8" />
          <p className="text-sm">You have no founders covered. Talk to PC to get assigned.</p>
        </div>
      </div>
    );
  }

  const breachCount = data.filter(f => f.slaStatus === "breach").length;
  const stuckCount = data.filter(f => f.activationState === "stuck").length;

  return (
    <div className="space-y-5">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Users className="w-7 h-7 text-primary" /> My founders
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Every founder you cover. Sorted by SLA breach severity so the founders most likely to be slipping bubble to the top.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-destructive/30 bg-destructive/10 text-destructive px-2.5 py-1">
            <ShieldCheck className="w-3 h-3" /> {breachCount} SLA breach
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300 px-2.5 py-1">
            <AlertTriangle className="w-3 h-3" /> {stuckCount} stuck
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border bg-muted text-muted-foreground px-2.5 py-1">
            <CheckCircle2 className="w-3 h-3" /> {data.length} total
          </span>
        </div>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Input
          placeholder="Search company or founder…"
          value={q}
          onChange={e => setQ(e.target.value)}
          className="w-64"
        />
        {cohorts.length > 1 && (
          <div className="flex gap-1.5">
            {cohorts.map(c => (
              <Button
                key={c.id}
                size="sm"
                variant={cohortFilter.has(c.id) ? "default" : "outline"}
                onClick={() => toggleCohort(c.id)}
              >
                {c.name}
              </Button>
            ))}
          </div>
        )}
        <select
          value={activationFilter}
          onChange={e => setActivationFilter(e.target.value as ActivationState | "all")}
          className="h-9 rounded-md border bg-background px-2 text-sm"
        >
          <option value="all">All activation</option>
          <option value="activated">Activated</option>
          <option value="at_risk">At risk</option>
          <option value="stuck">Stuck</option>
          <option value="onboarding">Onboarding</option>
        </select>
        <select
          value={slaFilter}
          onChange={e => setSlaFilter(e.target.value as SlaStatus | "all")}
          className="h-9 rounded-md border bg-background px-2 text-sm"
        >
          <option value="all">All SLA</option>
          <option value="breach">Breach</option>
          <option value="at_risk">At risk</option>
          <option value="in_sla">In SLA</option>
        </select>
      </div>

      {/* Table */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th onClick={() => toggleSort("company")} active={sortKey === "company"} dir={sortDir}>Founder · Company</Th>
              <Th onClick={() => toggleSort("activation")} active={sortKey === "activation"} dir={sortDir}>Activation</Th>
              <th className="text-left px-3 py-2 font-semibold">Signals</th>
              <Th onClick={() => toggleSort("slaSeverity")} active={sortKey === "slaSeverity"} dir={sortDir}>SLA</Th>
              <Th onClick={() => toggleSort("lastTouch")} active={sortKey === "lastTouch"} dir={sortDir}>Last touch</Th>
              <th className="text-left px-3 py-2 font-semibold">Cohort</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(f => (
              <tr
                key={f.companyId}
                onClick={() => setOpenRow(f)}
                className="border-t cursor-pointer hover:bg-muted/40 transition-colors"
              >
                <td className="px-3 py-2.5">
                  <div className="font-medium">{f.companyName}</div>
                  <div className="text-xs text-muted-foreground">{f.founderName ?? "(no founder on file)"}</div>
                </td>
                <td className="px-3 py-2.5">{activationBadge(f.activationState)}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1">
                    {signalChip(f.activationSignals.onboarding)}
                    {signalChip(f.activationSignals.readiness)}
                    {signalChip(f.activationSignals.checkIns)}
                    {signalChip(f.activationSignals.investorReady)}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    {slaBadge(f.slaStatus)}
                    <span className="text-xs text-muted-foreground">{f.slaTier}</span>
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <span className="font-mono text-xs">{fmtAgo(f.hoursSinceLastTouch)}</span>
                </td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">{f.cohortName ?? "—"}</td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground text-sm">No founders match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <FounderDetailDrawer open={!!openRow} onClose={() => setOpenRow(null)} founder={openRow} />
    </div>
  );
}

function Th({ children, onClick, active, dir }: { children: React.ReactNode; onClick: () => void; active: boolean; dir: SortDir }) {
  return (
    <th className="text-left px-3 py-2 font-semibold">
      <button onClick={onClick} className="inline-flex items-center gap-1 hover:text-foreground">
        {children}
        {active && (dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
      </button>
    </th>
  );
}
